/**
 * Per-seed pipe color (user request): the track's plastic body color is a
 * variation axis, not a fixed kit palette. Drawn from its own RNG family
 * (`pipe-color`) so it's deterministic per seed and touches NOTHING in
 * the generator — placements, goldens, and genVersion are unaffected; the
 * recolor happens at the texture level in the client (assets.ts swaps the
 * pipe swatch texels of the active colormap).
 */
import { rngFamily } from "../gen/rng.ts";

export type Rgb = readonly [number, number, number];

/** Pipe body color, or null to keep the kit's stock swatch. Loop mode
 * draws once per seed; endless draws PER ISLAND CHAPTER (user rule: the
 * pipe color is per island — the line launched from an island carries
 * its color until the next anchor). Pastel-to-rich range: saturation and
 * lightness bounded so the channel stays readable against every
 * atmosphere. */
export function drawPipeColor(
  seed: string,
  chapter: number | null = null,
): Rgb | null {
  if (chapter !== null && chapter < 0) return null; // before the home island
  const rng = rngFamily(
    seed,
    chapter === null ? "pipe-color" : `pipe-color/${chapter}`,
  );
  if (rng.chance(0.22)) return null; // classic kit look
  const h = rng.float();
  const s = 0.35 + rng.float() * 0.4;
  const l = 0.45 + rng.float() * 0.22;
  return hslToRgb(h, s, l);
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(channel(h + 1 / 3) * 255),
    Math.round(channel(h) * 255),
    Math.round(channel(h - 1 / 3) * 255),
  ];
}
